import React, { useContext, useEffect, useRef, useState } from 'react';

import type { TListingClient, TListingSellerInfo } from 'types/TListing';
import type { TSellerProfileClient } from 'types/TSellerProfile';

import EnvironmentContext from '../../../contexts/EnvironmentContext';
import getSellerProfile from '../../../repositories/getSellerProfile';
import getSellerOtherItems from '../../../repositories/getSellerOtherItems';

import SellerInfoSkeleton from './components/SellerInfoSkeleton';

import Classes from './SellerInfoExpanded.module.scss';

let SellerHeader;
let SellerTrustIndicators;
let SellerSalesRepresentatives;

type TSellerInfoExpandedProps = {
  seller: TListingSellerInfo;
  itemId?: string;
  categoryId?: number;
  showItemsCount: boolean;
  isCarAd: boolean;
  traits?: TListingClient['traits'];
};

const SellerInfoExpanded = ({
  seller,
  isCarAd,
  itemId,
  categoryId,
  showItemsCount,
  traits,
}: TSellerInfoExpandedProps) => {
  const {
    userDetails: { isAdOwner = false } = {},
    links: { sellerAnchor },
    hasLicensePlate,
  } = useContext(EnvironmentContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSalesRepresentativesLoaded, setIsSalesRepresentativesLoaded] = useState(false);
  const [profile, setProfile] = useState<TSellerProfileClient>({});
  const [itemsTotal, setItemsTotal] = useState(0);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const {
    id,
    name,
    activeSinceDiff,
    sellerProfileUrl,
    isSaved,
    sellerType,
    showDealerLegalDisclaimer,
    showProfilePicture,
    showSellerResponseRate,
    showSalesRepresentatives,
    showVerifications,
    sellerWebsiteDisplayUrl,
    sellerWebsiteUrl,
    allAdsUrl,
  } = seller;

  const showFranchiseDealerIndicator = !!traits?.includes('BRAND_DEALER_INDICATION');

  // SMB verified does not show for cars, as automotive is not included in the SMB bundles
  // refer to BNL-7414
  const isSmbVerified = !isCarAd && Boolean(profile.smbVerified);
  const isTrader = sellerType === 'TRADER';

  useEffect(() => {
    const elementRef = sectionRef.current as Element;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            (async () => {
              try {
                const [headerChunk, indicatorsChunk, fetchedProfile, fetchedOtherItems] = await Promise.all([
                  import(/* webpackChunkName: "SellerHeader" */ './components/SellerHeader'),
                  import(
                    /* webpackChunkName: "SellerTrustIndicators" */ './components/SellerTrustIndicators/SellerTrustIndicators'
                  ),
                  getSellerProfile({
                    sellerId: seller.id,
                    requestExternalReviews: seller.requestExternalReviews,
                    sellerName: seller.name,
                    postCode: seller.location?.postcode,
                  }),
                  showItemsCount && itemId && categoryId
                    ? getSellerOtherItems({ sellerId: id, itemId, categoryId })
                    : Promise.resolve(null),
                ]);
                SellerHeader = headerChunk.default;
                SellerTrustIndicators = indicatorsChunk.default;

                setProfile(fetchedProfile);
                setItemsTotal(fetchedOtherItems ? fetchedOtherItems.total : 0);
                setIsLoaded(true);
              } catch {}
            })();
          }
        });
      },
      {
        rootMargin: '0px 0px 100px 0px',
      },
    );

    if (elementRef && !isLoaded) {
      observer.observe(elementRef);
    }

    return () => {
      if (elementRef) {
        observer.disconnect();
      }
    };
  }, [
    categoryId,
    id,
    isLoaded,
    itemId,
    seller.id,
    seller.location?.postcode,
    seller.name,
    seller.requestExternalReviews,
    showItemsCount,
  ]);

  useEffect(() => {
    if (!isSalesRepresentativesLoaded && profile.salesRepresentatives?.length) {
      const loadComponent = async () => {
        try {
          SellerSalesRepresentatives = (
            await import(/* webpackChunkName: "SellerSalesRepresentatives" */ './components/SellerSalesRepresentatives')
          ).default;
          setIsSalesRepresentativesLoaded(true);
        } catch {}
      };

      loadComponent();
    }
  }, [isSalesRepresentativesLoaded, profile.salesRepresentatives?.length]);

  return (
    <section
      className={`${Classes.root} hz-Page-section hz-Page-section--separated`}
      ref={sectionRef}
      id={sellerAnchor}
    >
      {isLoaded ? (
        <>
          <SellerHeader
            id={id}
            name={name}
            activeSinceDiff={activeSinceDiff}
            sellerProfileUrl={sellerProfileUrl}
            isSaved={isSaved}
            isTrader={isTrader}
            isAdOwner={isAdOwner}
            isSmbVerified={isSmbVerified}
            showBackgroundPicture={showProfilePicture}
            {...profile.profilePictures}
          />

          <div className={Classes.contentWrapper}>
            <SellerTrustIndicators
              itemsTotal={itemsTotal}
              allAdsUrl={allAdsUrl}
              profile={profile}
              sellerId={id}
              sellerName={name}
              sellerType={sellerType}
              showDealerLegalDisclaimer={showDealerLegalDisclaimer}
              showItemsCount={showItemsCount}
              showSellerResponseRate={showSellerResponseRate}
              showVerifications={showVerifications}
              sellerWebsiteDisplayUrl={sellerWebsiteDisplayUrl}
              sellerWebsiteUrl={sellerWebsiteUrl}
              hasLicensePlate={hasLicensePlate}
              showFranchiseDealerIndicator={showFranchiseDealerIndicator}
            />

            {showSalesRepresentatives && isSalesRepresentativesLoaded && (
              <SellerSalesRepresentatives salesRepresentatives={profile.salesRepresentatives} />
            )}
          </div>
        </>
      ) : (
        <SellerInfoSkeleton />
      )}
    </section>
  );
};

export default SellerInfoExpanded;
